import { Injector, ViewChild, Directive, TemplateRef, HostListener } from '@angular/core';
import { ComponentType } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, FilterChangedEvent, GridOptions, GridReadyEvent, HeaderValueGetterParams, ModelUpdatedEvent, RowNode, ValueFormatterParams } from 'ag-grid-community';
import { AuthService, ServiceBase, SnackbarFactory, ThemeService } from '@shared/services';
import { CopyDataRendererComponent, CustomFilterComponent, CustomTooltipComponent } from '@shared/components';
import { DeleteDialogComponent } from '@shared/components';
import { map, Observable, of, Subscription, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as XLSX from 'xlsx';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, switchMap } from "rxjs/operators";
import { formatDate } from '@angular/common';

@Directive()
export abstract class GridBaseDirective<T, E> {
  private authCreatePostFix = '_CREATE';
  private authUpdatePostFix = '_UPDATE';
  private authDeletePostFix = '_DELETE';

  protected snackBar: SnackbarFactory;
  protected authService: AuthService;
  protected translateService: TranslateService;
  protected themeService: ThemeService;

  public authCreateRole: string;
  public authUpdateRole: string;
  public authDeleteRole: string;

  protected onlyNumbersRegex: RegExp = /^[0-9]*$/g;

  /**
   * Components you may reference when declaring cellRenderers for example.
   * Detailed explanation can be found here: {@link https://www.ag-grid.com/angular-data-grid/components/}
   */
  public components = {
    customFilter: CustomFilterComponent,
  };
  @ViewChild('agGrid') public agGrid: AgGridAngular<T, ColDef>;

  public selectedRow: T | null = null;
  public rowData: T[];
  public columnDefs: ColDef[] = [];
  public gridHiddenCols: string[] = [];
  public numericFields: string[] = [];
  public checkboxFields: string[] = [];
  public tooltipFields: string[] = [];
  public autosizeFields: string[] = [];
  public thousandFields: string[] = [];
  public multilineFields: string[] = [];
  public copyDataFields: string[] = [];

  protected $loadDataSubscription: Subscription;

  protected _autoSizeAllFields: boolean = false;

  public get autoSizeAllFields(): boolean {
    return this._autoSizeAllFields;
  }

  protected set autoSizeAllFields(val: boolean) {
    this._autoSizeAllFields = val;
  }

  private _renderMultiLine: boolean = true;

  public get renderMultiLine(): boolean {
    return this._renderMultiLine;
  }

  private set renderMultiLine(val: boolean) {
    this._renderMultiLine = val;
  }

  private headerHeight = 35;

  public gridOptions: GridOptions<T> = {
    defaultColDef: {
      width: 100,
      sortable: true,
      resizable: true,
      wrapText: false,
      autoHeight: false,
      filter: 'customFilter',
      cellStyle: {
        'white-space': 'nowrap',
        'line-height': '20px',
        'padding-left': '15px',
        'padding-right': '0px',
        'text-overflow': 'ellipsis',
        overflow: 'hidden',
        display: 'block',
      },
      headerValueGetter: this.translateHeader.bind(this),
    },
    accentedSort: true,
    animateRows: false,
    headerHeight: 50,
    suppressHorizontalScroll: false,
    enableCellTextSelection: true,
    pagination: false,
    paginationPageSize: 50,
    getRowStyle: (params) => {
      let isDeleted = params.data[Object.keys(params.data).find(d => d.toLowerCase().includes('deleted'))] ?? null;
      if (isDeleted || isDeleted == 1) {
        return { 'color': 'var(--color-warn)' }
      }
      else {
        return { 'color': 'unset' }
      }
    },
    getRowHeight: (_params) => {
      return 28;
    },
    getRowId: (_params) => {
      return _params.data[this.id] ?? null;
    },
    onGridReady: this.gridReady.bind(this),
    onRowClicked: this.rowClicked.bind(this),
    onFilterChanged: this.filterChanged.bind(this),
    onModelUpdated: this.modelUpdated.bind(this),
    onSelectionChanged: this.selectionChanged.bind(this),
    tooltipShowDelay: 0,
  };

  public selectFirstRow(): void {
    if (this.agGrid.api && this.agGrid.api.getDisplayedRowCount() > 0) {
      const firstRow = this.agGrid.api.getDisplayedRowAtIndex(0);
      firstRow.setSelected(true);
      this.selectedRow = firstRow.data;
    }
  }

  /**
   * @returns the id of the currently selected row.
   */
  get selectedId(): string {
    return this.selectedRow?.[this.id];
  }

  protected constructor(
    injector: Injector,
    public service: ServiceBase<T>,
    public dialog: MatDialog,
    public titleName: string,
    public id: string,
    public editComponent: ComponentType<E> | TemplateRef<E>
  ) {
    this.authCreateRole = titleName + this.authCreatePostFix;
    this.authUpdateRole = titleName + this.authUpdatePostFix;
    this.authDeleteRole = titleName + this.authDeletePostFix;

    this.authService = injector.get(AuthService);
    this.snackBar = injector.get(SnackbarFactory);
    this.translateService = injector.get(TranslateService);
    this.themeService = injector.get(ThemeService);

    this.translateService.onLangChange.subscribe(event => {
      this.agGrid?.api.refreshHeader();
    })
  }

  /* *************************
   *
   * Table functions
   *
   * ************************* */

  /**
   * Completely empties the grid and columnDefs
   */

  public emptyGrid() {
    this.columnDefs = [];
    this.rowData = [];
    this.selectedRow = null;
    this.agGrid?.api?.showNoRowsOverlay();
  }

  /**
   * Reloads the data by making a GET call to the component's base URL.
   *
   * @param params {HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; }} - Parameters that
   * will be passed as http query strings.
   */
  public loadData(
    params?:
      | HttpParams
      | {
        [param: string]:
        | string
        | number
        | boolean
        | ReadonlyArray<string | number | boolean>;
      },
    endpoint?: string
  ): void {
    this.showLoading();
    this.$loadDataSubscription?.unsubscribe();

    this.$loadDataSubscription = this.service.get(params, endpoint).subscribe({
      next: (data) => {
        if (data[0] == null) {
          this.emptyGrid();
        } else {
          this.columnDefs = this.dataToColumns(data);
          this.rowData = data;
        }
      },
      error: (err) => {
        this.snackBar.showButtonSnackbar(err);
      },
    });
  }

  /**
   * Converts a data sample to column definitions.
   *
   * @param data { {[key: string]: string | number | boolean} | T[] | null } - A data sample from
   * which the function can derive the column definitions or a null argument.
   * @returns The column definitions pertaining to the passed in data. In the case of null data, an
   * empty list is returned.
   */
  public dataToColumns(
    data: { [key: string]: string | number | boolean } | T[] | null | undefined,
    useNumericFieldAlign: boolean = true
  ): ColDef<T>[] {
    if (data === null || data === undefined || data.length === 0) return [];
    const dynamicColumnDefs: ColDef[] = [];

    Object.keys(data[0]).forEach((k) => {
      if (!this.checkboxFields.includes(k) && typeof (data[0][k]) == "boolean") {
        this.checkboxFields.push(k);
      }
      if (!this.numericFields.includes(k) && typeof (data[0][k]) == "number" /*&& this.onlyNumbersRegex.test(data[0][k].toString())*/) {
        this.numericFields.push(k);
      }
      if (!this.gridHiddenCols.includes(k) && (
          k.toLowerCase().startsWith('can') &&
          (data[0][k] == true || data[0][k] == false) &&           // == because of possible true or false and 1 or 0 checking, don't use === strict equality
          (typeof (data[0][k]) == "boolean" || typeof (data[0][k]) == "number")
        )
      ) {
        this.gridHiddenCols.push(k);
      }

      if (!this.gridHiddenCols.includes(k)) {
        let decideCellRenderer = (k: string) => {
          let cellRenderer = null;
          if (this.checkboxFields.includes(k)) {
            cellRenderer = this.renderCheckbox;
          }
          else if (this.multilineFields.includes(k) && this.renderMultiLine) {
            cellRenderer = this.renderMultiline;
          }
          else if (this.copyDataFields.includes(k)) {
            cellRenderer = CopyDataRendererComponent;
          }
          return cellRenderer;
        }

        let decideAutoHeight = (k: string): boolean => {
          return this.multilineFields.includes(k) && this.renderMultiLine;
        }

        dynamicColumnDefs.push({
          field: k,
          width: 100,
          cellRenderer: decideCellRenderer(k),
          type: this.numericFields.includes(k) && useNumericFieldAlign
            ? 'rightAligned'
            : null,
          valueFormatter: this.formatValue(k),
          tooltipValueGetter: (params) => { return this.tooltipFields.includes(k) ? params.value : null },
          tooltipComponent: CustomTooltipComponent,
          autoHeight: decideAutoHeight(k)
        });
      }
    });
    return dynamicColumnDefs;
  }

  public formatValue(k) {
    if (this.thousandFields.includes(k))
      return this.formatThousands
  }

  /**
   * Refreshes the selected row with the updated data. This resets the selection.
   *
   * @param {T} updatedData - The updated data that will be used.
   */
  public refreshRow(updatedData: T): void {
    const node = this.agGrid.api.getSelectedNodes()[0];
    const i = this.rowData.indexOf(node.data);
    this.rowData[i] = updatedData;
    this.agGrid.api.setRowData(this.rowData);
    this.agGrid.api.deselectAll();
  }

  /**
   * Refreshes the selected row with the updated data. Selection is kept with this method.
   *
   * @param {T} updatedData - The updated data that will be used.
   */
  public refreshRowWithKeepingSelected(updatedData: T): void {
    const node = this.agGrid.api.getSelectedNodes()[0];
    node.setData(updatedData);
    this.selectedRow = this.agGrid.api.getSelectedRows()[0];
  }

  /**
 * Refreshes the selected row with data gathered from service, with id as the parameter. Selection is kept with this method.
 * Uses provided id name and column with the same name from selectedRow
 *
 */
  public refreshOneRow() {
    this.service.get({ [this.id]: this.selectedRow[this.id] }).subscribe((data) => {
      this.refreshRowWithKeepingSelected(data[0]);
    });
  }

  /**
   * Click event handler for the grid.
   *
   * @param row {RowNode<T>} - The clicked node.
   */
  public rowClicked(row: RowNode<T>): void {
    this.selectedRow = row.data;
  }

  /*
   Contains grid header setups. If you want to override this method, make sure
   to call this one with super.gridReady()!
   */
  public gridReady(grid: GridReadyEvent<T>, selectFirstRow: boolean = true): void {
    grid.api.setGridOption('headerHeight', this.headerHeight);
    this.hideOverlay();
    this.sizeAllColumnsToFit();
    if (selectFirstRow as boolean) {
      this.selectFirstRow();
    }
  }

  /**
 * Filter changed event handler for the grid.
 * Deselects all rows when filter applied to grid.
 *
 * @param event {FilterChangedEvent<T>} - The filter change event.
 */
  public filterChanged(event: FilterChangedEvent<T>) {
    this.agGrid?.api.deselectAll();
    this.selectedRow = null;
  }

  /*
   Model changed event handler for grid.
   */
  public modelUpdated(event: ModelUpdatedEvent<T>, selectFirstRow: boolean = true) {
    this.sizeFields();
    if (selectFirstRow as boolean) {
      this.selectFirstRow();
    }
  }

  /*
   Selection changed event handler for grid.
   */
  public selectionChanged(): void {
    this.selectedRow = this.agGrid.api.getSelectedRows()[0] ?? null;
  }

  /*
   Sizes all columns to fit in the view.
   */
  public sizeAllColumnsToFit(): void {
    this.sizeFields();
  }

  /*
   Hides the grid.
   */
  public hideOverlay(): void {
    this.agGrid?.api.hideOverlay();
  }

  /*
   Shows loading screen on the grid.
   */
  public showLoading(): void {
    this.agGrid?.api.showLoadingOverlay();
  }

  /*
   Translated the header of the given column, it's made to be used
   in the HeaderValueGetter option of GridOptions.
   */
  private translateHeader(params: HeaderValueGetterParams<T>): string {
    const colDef = params.column.getColDef();
    const headerName = colDef.headerName ?? colDef.field;

    return this.translateService.instant(headerName);
  }


  /* *************************
   *
   * FORMATTERS - RENDERERS
   *
   * ************************* */

  public formatDateTime(params: ValueFormatterParams<T, string>): string {
    return params.value ? new Date(params.value).toLocaleString() : '';
  }

  /*
   Adds the "rounded" class to the inner text of the cell.
   */
  public roundedText(param: ValueFormatterParams<T, string>): string {
    const text =
      param.value.length > 25 ? param.value.slice(0, 22) + '...' : param.value;

    return `<span class="rounded" title="${param.value}">${text}</span>`;
  }

  /*
   If the cell contains a colour name, it colours the cell.
   */
  public renderColors(param: ValueFormatterParams<T, string>): string {
    return `<span style="background-color: ${param.value};" title="${param.value}"></span>`;
  }

  /*
   Material icon renderer.
   */
  public renderIcons(param: ValueFormatterParams<T, string>): string {
    return `<span><i class="material-icons" style="font-size: 18px; margin-top: 6px;">${param.value}</i></span>`;
  }

  /*
   Renders a checkbox for true/false cell values.
   */
  public renderCheckbox(params: ValueFormatterParams<T, string>): string {
    return `<input type="checkbox" disabled ${Boolean(params.value) ? 'checked' : ''
      } />`;
  }

  public renderMaterialIcon(params: ValueFormatterParams<any, string>): string {
    return params.value ? `<span class="material-symbols-outlined" name=${params.value}>${params.value}</span>` : null;
  }

  /*
  Formats the value into thousands, seperated by a space
  */
  public formatThousands(params: ValueFormatterParams<T, string>): string {
    return params.value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
  }

  /*
   Renders the value into multiple lines
   */
  public renderMultiline(params: ValueFormatterParams<T, string>): string {
    return params.value ? `<div>${params.value.split('\n').join('</br>')}</div>` : null;
  }

  /* *************************
   *
   * Controls
   *
   * ************************* */

  /**
   * Checks whether a user has a given right.
   *
   * @param right - The right.
   */
  public hasRight(right: string): boolean {
    return this.authService.isAuthenticated(right);
  }

  /**
   * Checks if the user has no right.
   *
   * @param right - The right.
   */
  public hasNoRight(right: string): boolean {
    return !this.hasRight(right)
  }

  /**
 * Checks whether a user has a given right - asynchronously.
 *
 * @param right - The right.
 */
  public hasRightAsync(right: string): Observable<boolean> {
    return this.authService.isAuthenticatedAsync(right);
  }

  /**
   * Checks if the user has no right  - asynchronously.
   *
   * @param right - The right.
   */
  public hasNoRightAsync(right: string): Observable<boolean> {
    return this.hasRightAsync(right)
      .pipe(
        map(hasRight => !hasRight)
      );
  }

  /**
   Opens a create dialog for the user.
 
   @param data {D} - Data to pass into the create dialog. Null is passed if left out.
   @param dialogWidth {string} - The width of the desired dialog, defaults to 600px.
   @param R - Defines the return type of the create dialog.
   @returns {Observable<R>} - Returns an observable that fires after the dialog is closed. The
   returned object depends on what the dialog returned.
 
   @example Simple create dialog with no parameters.
 
   this.create<boolean>().subscribe(
    (created) => {
     if(created) => this.loadData(filters);
    }
   );
 
   @example Create dialog followed by refreshing the grid after success.
 
   this.create<{success: boolean}>("create").subscribe(
    (result) => {
     if(result.success) this.loadData(filters);
    }
   );
 
   @example Create special configs on the fly that will be passed to the dialog.
 
   const specialConfig = {
    create: true,
    message: "HelloWorld"
   };
   this.create<{success: boolean, error: string}>(specialConfig).subscribe(
    (result) => {
     if(!result.success) console.error(result.error);
    }
   );
   */
  public create<R, D = any>(
    data: D = null,
    dialogWidth: string = '600px'
  ): Observable<R> {
    return this.hasNoRightAsync(this.authCreateRole)
      .pipe(
        switchMap(hasNoRight => {
          if (hasNoRight) {
            this.snackBar.showButtonSnackbarWithText(400, 'no-right');
            throw new HttpErrorResponse({ status: 400, statusText: 'no-right', error: 'no-right' });
          }

          return this.dialog.open<E, any, R>(this.editComponent, {
            panelClass: 'edit-dialog',
            width: dialogWidth,
            data,
          }).afterClosed();
        })
      );
  }

  /**
   Opens an edit dialog for the user.
 
   @param data {D} - Data to pass into the edit dialog.
   @param dialogWidth {string} - The width of the desired dialog, defaults to 600px;
   @param R - Defines the return type of the edit dialog.
   @returns {Observable<R>} - Returns an observable that fires after the dialog is closed. The
   returned object depends on what the dialog returned.
 
   @example Edit dialog followed by refreshing the edited model after success.
 
   this.edit<{success: boolean}>(this.selectedId).subscribe(
    (result) => {
     if(result.success) this.loadDataBy(this.selectedId);
    }
   );
 
   @example Create special configs on the fly that will be passed to the dialog.
 
   const specialConfig = {
    create: true,
    message: "HelloWorld"
   };
   this.edit<{success: boolean, error: string}>(specialConfig).subscribe(
    (result) => {
     if(!result.success) console.error(result.error);
    }
   );
   */
  public edit<R, D = any>(
    data: D,
    dialogWidth: string = '600px'
  ): Observable<R> {
    return this.hasNoRightAsync(this.authUpdateRole)
      .pipe(
        switchMap(hasNoRight => {
          if (hasNoRight) {
            this.snackBar.showButtonSnackbarWithText(400, 'no-right');
            throw new HttpErrorResponse({ status: 400, statusText: 'no-right', error: 'no-right' });
          }

          return this.dialog.open<E, any, R>(this.editComponent, {
            panelClass: 'edit-dialog',
            width: dialogWidth,
            data: data,
          }).afterClosed();
        })
      );
  }

  /**
   Opens a delete dialog for the user.
 
   @param message { string | undefined } - The message shown to the user when opened, it will be translated before shown.
   @param dialogWidth {string} - The width of the desired dialog, defaults to 600px.
   @returns {Observable<boolean>} - An observable that fires after the dialog is closed.
   The boolean sent is true if the user clicked on confirm, false otherwise.
 
   @example Delete dialog followed by refreshing the grid.
 
   this.delete().subscribe(
    (isSuccess) => {
     if(isSuccess) this.loadData();
    }
   );
 
   @example Delete dialog with custom message.
 
   this.delete("custom-message").subscribe(
    (isSuccess) => ...
   );
 
   */
  public delete(
    message?: string | { title: string, message: string },
    dialogWidth: string = '400px'
  ): Observable<boolean> {
    return this.hasNoRightAsync(this.authDeleteRole)
      .pipe(
        switchMap(hasNoRight => {
          if (hasNoRight) {
            this.snackBar.showButtonSnackbarWithText(400, 'no-right');
            throw new HttpErrorResponse({ status: 400, statusText: 'no-right', error: 'no-right' });
          }

          return this.dialog.open<DeleteDialogComponent, string | { title: string, message: string } | null, boolean>(
            DeleteDialogComponent, {
            width: dialogWidth,
            data: message,
          }).afterClosed()
            .pipe(
              switchMap(isSuccess => {
              if(isSuccess) {
                  return this.service.delete(this.selectedId);
                }
                return of(false);
              }),
              map(result => !!result),
              catchError(err => {
                this.snackBar.showButtonSnackbar(err);
                return of(false);
              })
            );
        })
      );
  }

  /**
   * Saves the grid in xlsx format, as seen on site, it will be downloaded into the computer's default Download folder.
   *
   * @param options
   */
  public excelExport(
    options?: { fileName?: string; sheetName?: string; }
  ): void {
    var sheetName = options?.sheetName ?? this.titleName;
    var name =
      (options?.fileName ?? this.titleName) + '_' +
      formatDate(new Date(), 'yyyy/MM/dd_HH:mm', 'en') +
      '.xlsx';
    let rowData = [];
    this.agGrid.api.forEachNodeAfterFilter((node) => {
      rowData.push({ ...node.data });
    });
    rowData = rowData.map((rowData) => {
      for (var key of Object.keys(rowData)) {
        if (this.gridHiddenCols.includes(key)) {
          delete rowData[key];
        }
      }
      return rowData;
    });
    rowData = rowData.map((rowData) => {
      var translatedKey = {};
      for (var key of Object.keys(rowData)) {
        translatedKey[this.translateService.instant(key)] = rowData[key];
      }
      return translatedKey;
    });
    var colOrder = this.columnDefs.filter(c => c.field.trim() != "").map(c => c.field);
    colOrder = colOrder.map((colOrder) => {
      return this.translateService.instant(colOrder);
    });
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rowData, {
      header: colOrder,
    });

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, name);
  }

  onAutosizeTogglePressed() {
    this.autoSizeAllFields = !this.autoSizeAllFields;
    this.sizeFields();
  }

  sizeFields() {
    this.agGrid?.api?.sizeColumnsToFit();
    let columnsToSize: string[] = [];
    if (this.autoSizeAllFields) {
      columnsToSize = [...this.columnDefs.filter(c => c.flex == undefined || c.flex == null).map(c => c.field)];
    }
    else {
      columnsToSize = [...this.autosizeFields];
    }
    this.agGrid?.api.autoSizeColumns(columnsToSize, false);
  }

  onFilterTextBoxChanged(filterValue: string) {
    this.agGrid.api.setQuickFilter(filterValue);
  }

  getAutoSizeAllFields(): boolean {
    return this.autoSizeAllFields;
  }

  getClass(): string {
    return `ag-theme-material${this.themeService.isDarkMode ? "-dark" : ''}`
  }

  onRenderMultiLineTogglePressed() {
    this.renderMultiLine = !this.renderMultiLine;

    this.columnDefs = this.dataToColumns(this.rowData);
    this.agGrid.api.redrawRows();
  }

  /**
   * Calls the grid resizer method when the size of the window is changed.
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.sizeFields();
  }
}
